import React, { useState } from "react";
import { MATERIALS, ROOM_TEMPLATES, ROOM_STROKES } from "../../../constants";
import { SYMBOL_LIBRARY } from "../../../constants/symbols";
import { mm2m } from "../../../utils/coords";
import { formatDistanceAuto, formatArea } from "../../../utils/units";
import { Row, SectionHead, EditRow, Divider, mono } from "./PanelAtoms";
import {
  mutRotateSymbol, mutLabelSymbol, mutDeleteSymbol,
  mutEditAnnotation, mutDeleteAnnotation, mutDeleteManualDim,
  mutScaleSymbol, mutUpdateSymbol,
  mutUpdateAnnotation, mutUpdateManualDim, mutUpdateGridLine,
  mutMoveAnnotation, mutMoveSymbol,
} from "../../../engine/layerMutations";
import { mutResizeWall, mutOffsetEdge, mutOffsetFace } from "../../../engine/mutations";
import {
  mutLockEdgeLength, mutUnlockEdge,
  mutMakePerpendicular, mutMakeParallel,
  mutMakeHorizontal, mutMakeVertical,
  removeConstraint, mutMakeEqualLength,
} from "../../../engine/constraintSolver";

const inr = n => "₹" + Math.round(n).toLocaleString("en-IN");

// ── Offset action for walls ───────────────────────────────────────────────────
function OffsetRow({ edgeId, commit }) {
  const [dist, setDist] = useState(500);
  return (
    <div style={{ padding:"3px 12px 6px" }}>
      <div style={{ fontSize:7.5, color:"#22d3ee", letterSpacing:1, marginBottom:3, ...mono }}>OFFSET</div>
      <div style={{ display:"flex", gap:3, alignItems:"center" }}>
        <input type="number" value={dist}
          onChange={e => setDist(Math.max(1, Number(e.target.value)))}
          style={{ flex:1, background:"#06111e", border:"1px solid #22d3ee30",
            color:"#22d3ee", borderRadius:2, padding:"2px 4px", fontSize:9, ...mono, textAlign:"right" }}
          min={1} max={50000} step={100}/>
        <span style={{ fontSize:8, color:"#1e2a36", ...mono }}>mm</span>
        <button onClick={() => commit(s => mutOffsetEdge(edgeId, dist, "left", s))}
          style={{ padding:"2px 8px", background:"transparent",
            border:"1px solid #22d3ee30", color:"#22d3ee",
            borderRadius:2, cursor:"pointer", fontSize:8, ...mono }} title="Offset left">◀</button>
        <button onClick={() => commit(s => mutOffsetEdge(edgeId, dist, "right", s))}
          style={{ padding:"2px 8px", background:"transparent",
            border:"1px solid #22d3ee30", color:"#22d3ee",
            borderRadius:2, cursor:"pointer", fontSize:8, ...mono }} title="Offset right">▶</button>
      </div>
    </div>
  );
}

const CON_LABELS = {
  LOCK_LENGTH:   "🔒 Lock length",
  HORIZONTAL:    "— Horizontal",
  VERTICAL:      "| Vertical",
  PERPENDICULAR: "⊥ Perpendicular",
  PARALLEL:      "∥ Parallel",
  LOCK_ANGLE:    "∠ Angle",
  EQUAL_LENGTH:  "= Equal length",
};

function ConstraintPanel({ edgeId, st, commit, selManyEdges }) {
  const [lockLen, setLockLen] = useState("");
  const constraints = Object.values(st.constraints || {})
    .filter(c => c.entities.includes(edgeId) && c.active !== false);

  // Second selected edge for two-wall constraints
  const otherEdge = (selManyEdges || []).find(id => id !== edgeId);

  return (
    <div>
      <SectionHead color="#60a5fa">CONSTRAINTS</SectionHead>

      {/* Active constraints list */}
      {constraints.length === 0 && (
        <div style={{ padding:"3px 12px 5px", fontSize:9, color:"#1e2a36", ...mono }}>None</div>
      )}
      {constraints.map(con => (
        <div key={con.id} style={{ display:"flex", alignItems:"center", gap:4,
          padding:"3px 12px", borderBottom:"1px solid #06111e" }}>
          <span style={{ flex:1, fontSize:9, color:"#60a5fa", ...mono }}>
            {CON_LABELS[con.type] || con.type}
            {con.params?.length && <span style={{ color:"#2a3a4a" }}> — {con.params.length}mm</span>}
            {con.params?.angle  && <span style={{ color:"#2a3a4a" }}> — {con.params.angle}°</span>}
          </span>
          <button onClick={() => commit(s => removeConstraint(con.id, s))}
            title="Remove constraint"
            style={{ background:"transparent", border:"none", color:"#f87171",
              cursor:"pointer", fontSize:11, padding:"0 3px", lineHeight:1 }}>✕</button>
        </div>
      ))}

      {/* Two-wall quick constraints */}
      {otherEdge && (
        <div style={{ padding:"4px 12px 3px", display:"flex", gap:3, flexWrap:"wrap" }}>
          <span style={{ fontSize:8, color:"#1e2a36", alignSelf:"center", ...mono }}>2 walls:</span>
          <button onClick={() => commit(s => mutMakePerpendicular(edgeId, otherEdge, s))}
            style={{ padding:"2px 8px", background:"#06111e", border:"1px solid #818cf830",
              color:"#818cf8", borderRadius:2, cursor:"pointer", fontSize:8, ...mono }}>
            ⊥ Perp
          </button>
          <button onClick={() => commit(s => mutMakeParallel(edgeId, otherEdge, s))}
            style={{ padding:"2px 8px", background:"#06111e", border:"1px solid #818cf830",
              color:"#818cf8", borderRadius:2, cursor:"pointer", fontSize:8, ...mono }}>
            ∥ Parallel
          </button>
          <button onClick={() => commit(s => mutMakeEqualLength(edgeId, otherEdge, s))}
            style={{ padding:"2px 8px", background:"#06111e", border:"1px solid #818cf830",
              color:"#818cf8", borderRadius:2, cursor:"pointer", fontSize:8, ...mono }}>
            = Equal
          </button>
        </div>
      )}

      {/* Single-wall constraints */}
      <div style={{ padding:"5px 12px 4px", display:"flex", flexWrap:"wrap", gap:3 }}>
        <div style={{ display:"flex", gap:2, width:"100%", marginBottom:2 }}>
          <input type="number" placeholder="mm (blank=current)"
            value={lockLen} onChange={e => setLockLen(e.target.value)}
            style={{ flex:1, background:"#06111e", border:"1px solid #60a5fa30",
              color:"#60a5fa", borderRadius:2, padding:"2px 4px", fontSize:8.5, ...mono }}
            min={1} max={50000} step={100}/>
          <button onClick={() => {
            const len = lockLen ? parseFloat(lockLen) : null;
            commit(s => mutLockEdgeLength(edgeId, len, s));
            setLockLen("");
          }} style={{ padding:"2px 8px", background:"#0c1828", border:"1px solid #60a5fa30",
            color:"#60a5fa", borderRadius:2, cursor:"pointer", fontSize:8, ...mono }}>
            🔒 Lock
          </button>
        </div>
        {[
          ["—H", () => commit(s => mutMakeHorizontal(edgeId, s))],
          ["|V", () => commit(s => mutMakeVertical(edgeId, s))],
        ].map(([label, fn]) => (
          <button key={label} onClick={fn}
            style={{ padding:"2px 8px", background:"#06111e", border:"1px solid #60a5fa30",
              color:"#60a5fa", borderRadius:2, cursor:"pointer", fontSize:8.5, ...mono }}>
            {label}
          </button>
        ))}
        <button onClick={() => commit(s => mutUnlockEdge(edgeId, s))}
          style={{ padding:"2px 8px", background:"transparent", border:"1px solid #f8717130",
            color:"#f87171", borderRadius:2, cursor:"pointer", fontSize:8, ...mono }}>
          Unlock all
        </button>
      </div>
    </div>
  );
}

// ── Wall ─────────────────────────────────────────────────────────────────────
export function WallPanel({ edge, wallM, sel, commit, onDelete, st, selMany, unitPreset, mode = "architectural" }) {
  const isArchitectural = mode === "architectural";
  
  return (
    <div>
      <SectionHead color="#3b82f6">WALL</SectionHead>

      <EditRow label={`Length (${unitPreset?.distance?.label || "m"})`} value={wallM.lenMm} type="number" min={100}
        onCommit={v => commit(s => mutResizeWall(sel.edge, v, s))}/>
      <EditRow label="Thickness (mm)" value={wallM.thickMm} type="number" min={50} max={600}
        onCommit={v => commit(s => ({...s,edges:{...s.edges,[sel.edge]:{...s.edges[sel.edge],thickness:v}}}))}/>
      
      {isArchitectural && (
        <EditRow label="Height (mm)" value={wallM.heightMm} type="number" min={1800} max={6000} step={100}
          onCommit={v => commit(s => ({...s,edges:{...s.edges,[sel.edge]:{...s.edges[sel.edge],height:v}}}))}/>
      )}

      {isArchitectural && (
        <>
          <Divider/>
          <SectionHead color="#2a3a4a">MATERIAL</SectionHead>
          <div style={{ padding:"3px 12px 4px", display:"grid", gridTemplateColumns:"1fr 1fr", gap:2 }}>
            {Object.values(MATERIALS).map(m => (
              <button key={m.id}
                onClick={() => commit(s => ({...s,edges:{...s.edges,[sel.edge]:{...s.edges[sel.edge],materialId:m.id,thickness:m.thickness}}}))}
                style={{ padding:"3px 5px", borderRadius:2, fontSize:8, cursor:"pointer", textAlign:"left",
                  background:(edge.materialId||"BRICK")===m.id?"#0c1828":"#06111e",
                  border:(edge.materialId||"BRICK")===m.id?"1px solid #3b82f640":"1px solid #06111e",
                  color:(edge.materialId||"BRICK")===m.id?"#60a5fa":"#3d5068", ...mono }}>
                <span style={{ display:"inline-block", width:6, height:6,
                  background:m.color, borderRadius:1, marginRight:3, verticalAlign:"middle" }}/>
                {m.name}
              </button>
            ))}
          </div>

          <Divider/>
          <SectionHead color="#2a3a4a">COSTS</SectionHead>
          <Row label="Material">{inr(wallM.matCost)}</Row>
          <Row label="Labour">{inr(wallM.labourCost)}</Row>
          <Row label="Total">{inr(wallM.totalCost)}</Row>
          {wallM.bricks > 0 && <Row label="Bricks">{wallM.bricks.toLocaleString("en-IN")}</Row>}
        </>
      )}

      <Divider/>
      <OffsetRow edgeId={sel.edge} commit={commit}/>
      {st && <ConstraintPanel edgeId={sel.edge} st={st} commit={commit} selManyEdges={selMany?.edges || []}/>}
      <div style={{ padding:"4px 12px 6px" }}>
        <button onClick={onDelete}
          style={{ width:"100%", padding:"4px", background:"#140808",
            border:"1px solid #2a0e0e", color:"#f87171",
            borderRadius:2, cursor:"pointer", fontSize:8.5, ...mono }}>
          Delete wall
        </button>
      </div>
    </div>
  );
}

// ── Room ─────────────────────────────────────────────────────────────────────
export function RoomPanel({ faceId, roomM, commit, unitPreset, mode = "architectural" }) {
  const isArchitectural = mode === "architectural";
  const [nameVal, setNameVal] = useState(roomM.rd.name || "");
  const [offDist, setOffDist] = useState(500);
  const save = () => commit(s => ({...s, rooms:{...s.rooms,[faceId]:{...s.rooms?.[faceId],name:nameVal}}}));

  return (
    <div>
      <SectionHead color={ROOM_STROKES[roomM.rd.type] || "#4ade80"}>
        {ROOM_TEMPLATES[roomM.rd.type]?.icon} ROOM
      </SectionHead>

      {isArchitectural && (
        <>
          <div style={{ padding:"3px 12px 5px" }}>
            <input value={nameVal} onChange={e => setNameVal(e.target.value)}
              onBlur={save} onKeyDown={e => e.key === "Enter" && save()}
              placeholder="Room name…"
              style={{ width:"100%", background:"#06111e", border:"1px solid #0d1520",
                color:"#dde6f0", borderRadius:2, padding:"3px 7px",
                fontSize:9.5, ...mono, outline:"none", boxSizing:"border-box" }}/>
          </div>

          <div style={{ padding:"0 12px 5px", display:"grid", gridTemplateColumns:"1fr 1fr", gap:2 }}>
            {Object.entries(ROOM_TEMPLATES).map(([k, t]) => (
              <button key={k}
                onClick={() => commit(s => ({...s,rooms:{...s.rooms,[faceId]:{type:k,name:nameVal}}}))}
                style={{ padding:"3px 5px", borderRadius:2, fontSize:7.5, cursor:"pointer", textAlign:"left",
                  background:roomM.rd.type===k?"#0c1828":"#06111e",
                  border:roomM.rd.type===k?`1px solid ${ROOM_STROKES[k]||"#4ade80"}40`:"1px solid #06111e",
                  color:roomM.rd.type===k?(ROOM_STROKES[k]||"#4ade80"):"#3d5068", ...mono }}>
                {t.icon} {t.label}
              </button>
            ))}
          </div>

          <Divider/>
        </>
      )}
      
      <Row label="Area">{formatArea(roomM.areaM2 * 1e6, unitPreset)}</Row>
      <Row label="Perimeter">{formatDistanceAuto(roomM.perimMm, unitPreset)}</Row>
      
      {isArchitectural && (
        <>
          <Row label="Floor cost">{inr(roomM.floorCost)}</Row>
          <Row label="Finish cost">{inr(roomM.wallCost)}</Row>
          <Row label="Total">{inr(roomM.totalCost)}</Row>
        </>
      )}

      <Divider/>
      <SectionHead color="#22d3ee">OFFSET ROOM</SectionHead>
      <div style={{ padding:"3px 12px 6px", display:"flex", gap:3, alignItems:"center" }}>
        <input type="number" value={offDist}
          onChange={e => setOffDist(Math.max(1, Number(e.target.value)))}
          style={{ flex:1, background:"#06111e", border:"1px solid #22d3ee30",
            color:"#22d3ee", borderRadius:2, padding:"2px 4px", fontSize:9, ...mono }}
          min={1} step={100}/>
        <span style={{ fontSize:8, color:"#1e2a36", ...mono }}>mm</span>
        <button onClick={() => commit(s => mutOffsetFace(faceId, offDist, s))}
          style={{ flex:1, padding:"3px 4px", background:"transparent",
            border:"1px solid #22d3ee30", color:"#22d3ee",
            borderRadius:2, cursor:"pointer", fontSize:8, ...mono }}>↑ Out</button>
        <button onClick={() => commit(s => mutOffsetFace(faceId, -offDist, s))}
          style={{ flex:1, padding:"3px 4px", background:"transparent",
            border:"1px solid #22d3ee30", color:"#22d3ee",
            borderRadius:2, cursor:"pointer", fontSize:8, ...mono }}>↓ In</button>
      </div>
    </div>
  );
}

// ── Symbol ────────────────────────────────────────────────────────────────────
export function SymbolPanel({ sym, sel, commit }) {
  const def = SYMBOL_LIBRARY[sym.type];
  const [rot, setRot] = useState(sym.rotation || 0);

  const applyRot = v => {
    setRot(v);
    commit(s => mutRotateSymbol(sel.symbol, v, s));
  };

  return (
    <div>
      <SectionHead color="#facc15">SYMBOL — {def?.label || sym.type}</SectionHead>

      <EditRow label="X position (mm)" value={Math.round(sym.x)} type="number"
        onCommit={v => commit(s => mutUpdateSymbol(sel.symbol, { x: v }, s))}/>
      <EditRow label="Y position (mm)" value={Math.round(sym.y)} type="number"
        onCommit={v => commit(s => mutUpdateSymbol(sel.symbol, { y: v }, s))}/>

      <div style={{ padding:"3px 12px 4px" }}>
        <div style={{ fontSize:7.5, color:"#2a3a4a", marginBottom:3, ...mono }}>ROTATION (°)</div>
        <div style={{ display:"flex", gap:3, alignItems:"center" }}>
          <input type="range" min={0} max={360} step={1} value={rot}
            onChange={e => setRot(Number(e.target.value))}
            onMouseUp={e => applyRot(Number(e.target.value))}
            onTouchEnd={e => applyRot(rot)}
            style={{ flex:1, accentColor:"#facc15" }}/>
          <input type="number" min={0} max={360} value={rot}
            onChange={e => applyRot(Number(e.target.value) % 360)}
            style={{ width:42, background:"#06111e", border:"1px solid #facc1530",
              color:"#facc15", borderRadius:2, padding:"2px 4px", fontSize:9, ...mono, textAlign:"right" }}/>
          <span style={{ fontSize:8, color:"#1e2a36", ...mono }}>°</span>
        </div>
        <div style={{ display:"flex", gap:2, marginTop:3 }}>
          {[0,45,90,135,180,270].map(r => (
            <button key={r} onClick={() => applyRot(r)}
              style={{ flex:1, padding:"2px 0", fontSize:7.5, borderRadius:2, cursor:"pointer",
                background:rot===r?"#1a1400":"#06111e",
                border:rot===r?"1px solid #facc1540":"1px solid #06111e",
                color:rot===r?"#facc15":"#3d5068", ...mono }}>{r}°</button>
          ))}
        </div>
      </div>

      <div style={{ padding:"3px 12px 4px" }}>
        <div style={{ fontSize:7.5, color:"#2a3a4a", marginBottom:3, ...mono }}>SIZE (scale)</div>
        <div style={{ display:"flex", gap:3, alignItems:"center" }}>
          <input type="range" min={0.25} max={4} step={0.05} value={sym.scale || 1}
            onChange={e => commit(s => mutScaleSymbol(sel.symbol, Number(e.target.value), s))}
            style={{ flex:1, accentColor:"#facc15" }}/>
          <span style={{ fontSize:9, color:"#facc15", width:28, textAlign:"right", ...mono }}>
            {((sym.scale || 1) * 100).toFixed(0)}%
          </span>
        </div>
      </div>

      <div style={{ padding:"3px 12px 4px" }}>
        <div style={{ fontSize:7.5, color:"#2a3a4a", marginBottom:3, ...mono }}>LABEL</div>
        <input value={sym.label || ""} placeholder="Label…"
          onChange={e => commit(s => mutLabelSymbol(sel.symbol, e.target.value, s))}
          style={{ width:"100%", background:"#06111e", border:"1px solid #0d1520",
            color:"#dde6f0", borderRadius:2, padding:"3px 7px",
            fontSize:9.5, ...mono, outline:"none", boxSizing:"border-box" }}/>
      </div>

      <div style={{ padding:"4px 12px 6px" }}>
        <button onClick={() => commit(s => mutDeleteSymbol(sel.symbol, s))}
          style={{ width:"100%", padding:"4px", background:"#140808",
            border:"1px solid #2a0e0e", color:"#f87171",
            borderRadius:2, cursor:"pointer", fontSize:8.5, ...mono }}>
          Delete symbol
        </button>
      </div>
    </div>
  );
}

// ── Annotation ────────────────────────────────────────────────────────────────
export function AnnotationPanel({ selAnnotation, st, commit }) {
  const isAnn = selAnnotation.startsWith("ann_");
  const isDim = selAnnotation.startsWith("dim_");
  const isGl  = selAnnotation.startsWith("gl_");
  const id    = isGl ? selAnnotation : selAnnotation.slice(4);

  const ann = isAnn ? (st.annotations || {})[id]   : null;
  const dim = isDim ? (st.manualDims  || {})[id]   : null;
  const gl  = isGl  ? (st.gridLines   || {})[selAnnotation] : null;

  if (ann) return (
    <div>
      <SectionHead color="#94a3b8">ANNOTATION</SectionHead>

      <div style={{ padding:"3px 12px 5px" }}>
        <div style={{ fontSize:7.5, color:"#2a3a4a", marginBottom:2, ...mono }}>TEXT</div>
        <textarea value={ann.text || ""}
          onChange={e => commit(s => mutEditAnnotation(id, e.target.value, s))}
          rows={2}
          style={{ width:"100%", background:"#06111e", border:"1px solid #0d1520",
            color:"#dde6f0", borderRadius:2, padding:"3px 7px", resize:"vertical",
            fontSize:9.5, ...mono, outline:"none", boxSizing:"border-box" }}/>
      </div>

      <div style={{ padding:"0 12px 4px", display:"flex", gap:3 }}>
        {[["text","Text"],["callout","Callout"]].map(([v,l]) => (
          <button key={v}
            onClick={() => commit(s => mutUpdateAnnotation(id, { style: v }, s))}
            style={{ flex:1, padding:"3px", background:ann.style===v?"#0c1828":"#06111e",
              border:ann.style===v?"1px solid #94a3b840":"1px solid #06111e",
              color:ann.style===v?"#94a3b8":"#3d5068",
              borderRadius:2, cursor:"pointer", fontSize:8, ...mono }}>{l}</button>
        ))}
      </div>

      <EditRow label="Font size" value={ann.fontSize || 12} type="number" min={6} max={72}
        onCommit={v => commit(s => mutUpdateAnnotation(id, { fontSize: v }, s))}/>
      <EditRow label="X position" value={Math.round(ann.x)} type="number"
        onCommit={v => commit(s => mutUpdateAnnotation(id, { x: v }, s))}/>
      <EditRow label="Y position" value={Math.round(ann.y)} type="number"
        onCommit={v => commit(s => mutUpdateAnnotation(id, { y: v }, s))}/>

      <div style={{ padding:"4px 12px 6px" }}>
        <button onClick={() => commit(s => mutDeleteAnnotation(id, s))}
          style={{ width:"100%", padding:"4px", background:"#140808",
            border:"1px solid #2a0e0e", color:"#f87171",
            borderRadius:2, cursor:"pointer", fontSize:8.5, ...mono }}>
          Delete annotation
        </button>
      </div>
    </div>
  );

  if (dim) return (
    <div>
      <SectionHead color="#818cf8">DIMENSION</SectionHead>

      <div style={{ padding:"3px 12px 5px" }}>
        <div style={{ fontSize:7.5, color:"#2a3a4a", marginBottom:2, ...mono }}>TEXT OVERRIDE</div>
        <input value={dim.text || ""}
          onChange={e => commit(s => mutUpdateManualDim(id, { text: e.target.value }, s))}
          placeholder="auto"
          style={{ width:"100%", background:"#06111e", border:"1px solid #0d1520",
            color:"#dde6f0", borderRadius:2, padding:"3px 7px",
            fontSize:9.5, ...mono, outline:"none", boxSizing:"border-box" }}/>
      </div>

      <EditRow label="Offset (px)" value={dim.offset || 20} type="number" min={5} max={200}
        onCommit={v => commit(s => mutUpdateManualDim(id, { offset: v }, s))}/>
      <Row label="Length">{Math.round(Math.sqrt((dim.ptB.x-dim.ptA.x)**2+(dim.ptB.y-dim.ptA.y)**2))} mm</Row>

      <div style={{ padding:"4px 12px 6px" }}>
        <button onClick={() => commit(s => mutDeleteManualDim(id, s))}
          style={{ width:"100%", padding:"4px", background:"#140808",
            border:"1px solid #2a0e0e", color:"#f87171",
            borderRadius:2, cursor:"pointer", fontSize:8.5, ...mono }}>
          Delete dimension
        </button>
      </div>
    </div>
  );

  if (gl) return (
    <div>
      <SectionHead color="#f97316">GRID LINE</SectionHead>

      <div style={{ padding:"3px 12px 5px" }}>
        <div style={{ fontSize:7.5, color:"#2a3a4a", marginBottom:2, ...mono }}>LABEL</div>
        <input value={gl.label || ""}
          onChange={e => commit(s => mutUpdateGridLine(selAnnotation, { label: e.target.value }, s))}
          style={{ width:"100%", background:"#06111e", border:"1px solid #0d1520",
            color:"#f97316", borderRadius:2, padding:"3px 7px",
            fontSize:9.5, ...mono, outline:"none", boxSizing:"border-box" }}/>
      </div>

      <EditRow label="Position (mm)" value={Math.round(gl.position)} type="number"
        onCommit={v => commit(s => mutUpdateGridLine(selAnnotation, { position: v }, s))}/>
      <Row label="Axis">{gl.axis?.toUpperCase()}</Row>

      <div style={{ padding:"4px 12px 6px" }}>
        <button onClick={() => commit(s => {
          const gls = { ...(s.gridLines || {}) }; delete gls[selAnnotation];
          return { ...s, gridLines: gls };
        })} style={{ width:"100%", padding:"4px", background:"#140808",
            border:"1px solid #2a0e0e", color:"#f87171",
            borderRadius:2, cursor:"pointer", fontSize:8.5, ...mono }}>
          Delete grid line
        </button>
      </div>
    </div>
  );

  return null;
}
